import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/pfc";
import { ShoppingBag, Store, ExternalLink, Tag, Filter } from "lucide-react";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "official", label: "PFC Official" },
  { key: "apparel", label: "Apparel" },
  { key: "accessories", label: "Accessories" },
];

export default function Marketplace() {
  const [products, setProducts] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "PFC Marketplace — Public Figures Campaign";
    (async () => {
      try {
        const [pRes, vRes] = await Promise.all([
          api.get("/marketplace/products"),
          api.get("/marketplace/vendors"),
        ]);
        setProducts(pRes.data || []);
        setVendors(vRes.data || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return products;
    if (filter === "official") return products.filter((p) => p.vendor_id === "vendor_official_pfc");
    return products.filter((p) => p.category === filter);
  }, [products, filter]);

  return (
    <div className="bg-ink-900 min-h-screen text-white" data-testid="marketplace-page">
      <div className="relative border-b border-white/10 bg-gradient-to-b from-black via-ink-900 to-ink-900">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-16 lg:py-20">
          <div className="flex items-center gap-2 text-pfc-gold font-sub uppercase tracking-[0.35em] text-xs mb-4">
            <ShoppingBag size={14} /> Marketplace · Official Merch + Vendor Storefronts
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-3xl">
            Wear the movement. Build the storefront.
          </h1>
          <p className="text-white/70 mt-5 max-w-2xl text-base">
            Shop the Official PFC Collection or discover independent vendors who build, design, and ship on PFC.
            Sellers — open your own storefront for <span className="text-pfc-gold">$99 activation + $9.99/mo</span>.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link
              to="/vendor/register"
              className="inline-flex items-center gap-2 px-5 py-3 bg-pfc-gold text-ink-900 font-sub uppercase tracking-[0.2em] text-xs hover:bg-pfc-gold-bright transition"
              data-testid="open-storefront-cta"
            >
              <Store size={14} /> Open Your Storefront
            </Link>
            <a
              href="#products"
              className="inline-flex items-center gap-2 px-5 py-3 border border-white/20 text-white font-sub uppercase tracking-[0.2em] text-xs hover:bg-white hover:text-ink-900 transition"
              data-testid="browse-shop-cta"
            >
              <Tag size={14} /> Browse the Shop
            </a>
          </div>
        </div>
      </div>

      <div id="products" className="max-w-[1400px] mx-auto px-5 lg:px-10 py-14">
        {/* Filters */}
        <div className="flex items-center justify-between gap-4 mb-8 flex-wrap">
          <div className="flex items-center gap-3 text-white/60 text-xs uppercase tracking-[0.3em] font-sub">
            <Filter size={14} /> Filter
          </div>
          <div className="flex gap-2 flex-wrap" data-testid="marketplace-filters">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                data-testid={`filter-${f.key}`}
                className={`px-4 py-2 font-sub uppercase text-[11px] tracking-[0.25em] border transition ${
                  filter === f.key
                    ? "bg-pfc-gold text-ink-900 border-pfc-gold"
                    : "border-white/15 text-white/70 hover:bg-white/5"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="text-white/40 py-20 text-center">Loading the shop…</div>
        ) : filtered.length === 0 ? (
          <div className="text-white/40 py-20 text-center" data-testid="empty-products">
            No products match this filter.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="products-grid">
            {filtered.map((p) => (
              <ProductCard key={p.product_id} product={p} />
            ))}
          </div>
        )}

        {/* Vendors strip */}
        {vendors.length > 0 && (
          <div className="mt-20" data-testid="vendors-strip">
            <div className="flex items-baseline justify-between mb-6">
              <h2 className="font-display text-2xl tracking-tight">PFC Vendors</h2>
              <Link to="/vendor/register" className="text-pfc-gold text-xs font-sub uppercase tracking-[0.3em] hover:underline">
                Become a Vendor →
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {vendors.map((v) => (
                <Link
                  key={v.vendor_id}
                  to={`/marketplace/vendor/${v.slug}`}
                  className="block border border-white/10 bg-ink-800/40 p-5 hover:border-pfc-gold transition group"
                  data-testid={`vendor-card-${v.slug}`}
                >
                  <div className="flex items-center gap-3 mb-3">
                    {v.logo ? (
                      <img src={v.logo} alt={v.business_name} className="h-10 w-10 object-contain bg-black/30 p-1" />
                    ) : (
                      <div className="h-10 w-10 bg-white/5 grid place-items-center text-pfc-gold">
                        <Store size={18} />
                      </div>
                    )}
                    <div>
                      <div className="font-sub uppercase text-sm text-white group-hover:text-pfc-gold transition">
                        {v.business_name}
                      </div>
                      {v.is_official && (
                        <div className="text-[10px] text-pfc-gold tracking-[0.25em] uppercase">Official</div>
                      )}
                    </div>
                  </div>
                  {v.tagline && <p className="text-white/60 text-sm">{v.tagline}</p>}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ProductCard({ product }) {
  const img = (product.images || [])[0];
  return (
    <Link
      to={`/marketplace/product/${product.product_id}`}
      className="block group border border-white/10 bg-ink-800/40 overflow-hidden hover:border-pfc-gold transition"
      data-testid={`product-card-${product.product_id}`}
    >
      <div className="aspect-square bg-white/5 overflow-hidden">
        {img ? (
          <img src={img} alt={product.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
        ) : (
          <div className="w-full h-full grid place-items-center text-white/30">
            <ShoppingBag size={48} />
          </div>
        )}
      </div>
      <div className="p-5">
        <div className="text-[10px] tracking-[0.3em] uppercase font-sub text-pfc-gold mb-2">
          {product.vendor_name}
          {product.external_url && (
            <span className="ml-2 inline-flex items-center gap-1 text-white/40">
              <ExternalLink size={10} /> PAOM
            </span>
          )}
        </div>
        <h3 className="font-display text-lg text-white group-hover:text-pfc-gold transition mb-2">
          {product.title}
        </h3>
        <div className="flex items-center justify-between mt-3">
          <span className="text-pfc-gold font-display text-xl">${product.price.toFixed(2)}</span>
          <span className="text-[11px] uppercase tracking-[0.3em] text-white/50 font-sub">View →</span>
        </div>
      </div>
    </Link>
  );
}
